import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import { initTracking } from './services/tracking';
import api from './api';

// Critical path — loaded eagerly
import Landing        from './pages/Landing';
import Layout         from './components/Layout';
import CookieBanner   from './components/CookieBanner';

// Code-split — loaded on demand
const Login           = React.lazy(() => import('./pages/Login'));
const Register        = React.lazy(() => import('./pages/Register'));
const JobPublicPage   = React.lazy(() => import('./pages/JobPublicPage'));
const JobsPage        = React.lazy(() => import('./pages/JobsPage'));
const ForgotPassword  = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword       = React.lazy(() => import('./pages/ResetPassword'));
const VerifyEmail         = React.lazy(() => import('./pages/VerifyEmail'));
const VerifyEmailPending  = React.lazy(() => import('./pages/VerifyEmailPending'));
const OnboardingRecruiter = React.lazy(() => import('./pages/OnboardingRecruiter'));
const OnboardingCandidate = React.lazy(() => import('./pages/OnboardingCandidate'));
const Explore             = React.lazy(() => import('./pages/Explore'));
const Matches             = React.lazy(() => import('./pages/Matches'));
const Messages            = React.lazy(() => import('./pages/Messages'));
const Profile             = React.lazy(() => import('./pages/Profile'));
const PublicProfile       = React.lazy(() => import('./pages/PublicProfile'));
const Stats               = React.lazy(() => import('./pages/Stats'));
const Recruiter           = React.lazy(() => import('./pages/Recruiter'));
const Billing             = React.lazy(() => import('./pages/Billing'));
const BiasAudit           = React.lazy(() => import('./pages/BiasAudit'));
const GDPR                = React.lazy(() => import('./pages/GDPR'));
const Referral            = React.lazy(() => import('./pages/Referral'));
const Boost               = React.lazy(() => import('./pages/Boost'));
const Community           = React.lazy(() => import('./pages/Community'));
const LandingDomain       = React.lazy(() => import('./pages/LandingDomain'));
const AcquisitionDashboard = React.lazy(() => import('./pages/AcquisitionDashboard'));
const CandidateAnalytics  = React.lazy(() => import('./pages/CandidateAnalytics'));
const Help                = React.lazy(() => import('./pages/Help'));
const About               = React.lazy(() => import('./pages/About'));
const Support             = React.lazy(() => import('./pages/Support'));
const TermsOfService      = React.lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy       = React.lazy(() => import('./pages/PrivacyPolicy'));
const RecruiterPreview    = React.lazy(() => import('./pages/RecruiterPreview'));
const TeamChat            = React.lazy(() => import('./pages/TeamChat'));
const Search              = React.lazy(() => import('./pages/Search'));
const Interview           = React.lazy(() => import('./pages/Interview'));
const NotFound            = React.lazy(() => import('./pages/NotFound'));
const Unsubscribe         = React.lazy(() => import('./pages/Unsubscribe'));
const SwipeDemoPage       = React.lazy(() => import('./pages/SwipeDemoPage'));

// Init tracking après les imports
try { initTracking(); } catch(e) { console.warn('[Tracking] Init failed:', e.message); }

function PrivateRoute({ children, role, landing }) {
  const { user, loading } = useAuth();

  // Demande push permission une fois après login
  const [pushRequested, setPushRequested] = React.useState(false);
  React.useEffect(() => {
    if (user && !pushRequested) {
      setPushRequested(true);
      setTimeout(() => {
        import('./firebase').then(({ requestPushPermission }) =>
          requestPushPermission(api).catch(() => {})
        );
      }, 3000);
    }
  }, [user]);

  // Notifications foreground (app ouverte)
  React.useEffect(() => {
    if (!user) return;
    let unsub;
    import('./firebase').then(({ onForegroundMessage }) => {
      unsub = onForegroundMessage(({ notification }) => {
        if (notification?.title) {
          import('react-hot-toast').then(({ default: toast }) => {
            toast(`${notification.title}${notification.body ? ` — ${notification.body}` : ''}`);
          });
        }
      });
    });
    return () => typeof unsub === 'function' && unsub();
  }, [user]);

  if (loading) return <div className="splash"><div className="spinner"/></div>;
  if (!user)   return landing ? <Landing /> : <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="splash"><div className="spinner"/></div>;
  if (user)    return <Navigate to={user.role === 'recruiter' ? '/recruiter' : '/explore'} replace />;
  return children;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function JobsSlugDispatcher() {
  const { id } = useParams();
  if (UUID_RE.test(id)) return <JobPublicPage />;
  return <JobsPage />;
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  const showCandidateOnboarding = user?.role === 'candidate' && user?.onboarding_done === false;
  const showRecruiterOnboarding = user?.role === 'recruiter' && user?.onboarding_done === false;

  const isLegalPage = location.pathname === '/terms' || location.pathname === '/privacy';

  if (showCandidateOnboarding && location.pathname !== '/onboarding' && !isLegalPage) {
    return <Navigate to="/onboarding" replace />;
  }
  if (showRecruiterOnboarding && location.pathname !== '/recruiter/onboarding' && !isLegalPage) {
    return <Navigate to="/recruiter/onboarding" replace />;
  }

  return (
    <ErrorBoundary>
      <React.Suspense fallback={<div className="splash"><div className="spinner"/></div>}>
      <Routes>
        {/* Public */}
        <Route path="/login"                    element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register"                 element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password"          element={<ForgotPassword />} />
        <Route path="/reset-password"           element={<ResetPassword />} />
        <Route path="/verify-email"             element={<VerifyEmail />} />
        <Route path="/verify-email-pending"     element={<VerifyEmailPending />} />
        <Route path="/analytics"                element={<CandidateAnalytics />} />
        <Route path="/recruiter/onboarding"     element={<OnboardingRecruiter />} />
        <Route path="/l/:domain"                element={<LandingDomain />} />
        <Route path="/acquisition"              element={<AcquisitionDashboard />} />
        <Route path="/about"                    element={<About />} />
        <Route path="/community"                element={<Community />} />
        <Route path="/help"                     element={<Help />} />
        <Route path="/support"                  element={<Support />} />
        <Route path="/terms"                    element={<TermsOfService />} />
        <Route path="/privacy"                  element={<PrivacyPolicy />} />
        <Route path="/onboarding"               element={<OnboardingCandidate />} />
        <Route path="/recruiter-preview/:token" element={<RecruiterPreview />} />
        <Route path="/unsubscribe"              element={<Unsubscribe />} />
        <Route path="/demo"                     element={<SwipeDemoPage />} />
        <Route path="/u/:candidateId"           element={<PublicProfile />} />
        <Route path="/jobs"                     element={<JobsPage />} />
        <Route path="/jobs/:id"                 element={<JobsSlugDispatcher />} />

        {/* App shell — landing pour visiteurs, dashboard pour connectés */}
        <Route path="/" element={<PrivateRoute landing><Layout /></PrivateRoute>}>
          <Route index element={
            user?.role === 'recruiter'
              ? <Navigate to="/recruiter" replace />
              : <Navigate to="/explore" replace />
          }/>
          {/* Candidate */}
          <Route path="explore"   element={<PrivateRoute role="candidate"><Explore /></PrivateRoute>}/>
          <Route path="interview" element={<PrivateRoute role="candidate"><Interview /></PrivateRoute>}/>
          <Route path="boost"     element={<PrivateRoute role="candidate"><Boost /></PrivateRoute>}/>
          {/* Recruiter */}
          <Route path="recruiter"  element={<PrivateRoute role="recruiter"><Recruiter /></PrivateRoute>}/>
          <Route path="billing"    element={<PrivateRoute role="recruiter"><Billing /></PrivateRoute>}/>
          <Route path="bias-audit" element={<PrivateRoute role="recruiter"><BiasAudit /></PrivateRoute>}/>
          <Route path="team-chat"  element={<PrivateRoute role="recruiter"><TeamChat /></PrivateRoute>}/>
          {/* Shared */}
          <Route path="matches"              element={<PrivateRoute><Matches /></PrivateRoute>}/>
          <Route path="messages"             element={<PrivateRoute><Messages /></PrivateRoute>}/>
          <Route path="messages/:matchId"    element={<PrivateRoute><Messages /></PrivateRoute>}/>
          <Route path="stats"                element={<PrivateRoute><Stats /></PrivateRoute>}/>
          <Route path="profile"              element={<PrivateRoute><Profile /></PrivateRoute>}/>
          <Route path="profile/:candidateId" element={<PrivateRoute><PublicProfile /></PrivateRoute>}/>
          <Route path="search"               element={<PrivateRoute><Search /></PrivateRoute>}/>
          <Route path="referral"             element={<PrivateRoute><Referral /></PrivateRoute>}/>
          <Route path="gdpr"                 element={<PrivateRoute><GDPR /></PrivateRoute>}/>
        </Route>

        <Route path="*" element={<NotFound />}/>
      </Routes>
      </React.Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <CookieBanner />
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: '#ffffff',
              color: '#191919',
              border: '1px solid #e0ddd6',
              borderRadius: '12px',
              fontSize: '.85rem',
              boxShadow: '0 4px 12px rgba(0,0,0,.1)',
            },
            success: { iconTheme: { primary: '#057642', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#cc1016', secondary: '#fff' } },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
    </GoogleOAuthProvider>
  );
}
